<template>
  <div>
    <!-- ✅ Banner principal SOLO si visible -->
    <div v-if="visible">
      <div class="cookie-overlay" @click="onOverlayClick"></div>

      <div class="cookie-center" role="dialog" aria-modal="true" aria-label="Consentimiento de cookies">
        <div class="cookie-card">
          <div class="cookie-head">
            <div class="cookie-title">🍪 Cookies en Quanty</div>

            <button class="cookie-x" type="button" aria-label="Cerrar" @click="rejectAll">×</button>
          </div>

          <div class="cookie-desc">
            Usamos cookies necesarias para el funcionamiento y, si nos lo permites, cookies analíticas para mejorar la experiencia.
            Puedes cambiar tu elección cuando quieras.
            <router-link class="cookie-link" to="/politica-cookies">Ver política</router-link>
          </div>

          <div class="cookie-actions">
            <button class="btn-cookie btn-cookie--outline" type="button" @click="openPreferences">
              Configurar
            </button>
            <button class="btn-cookie btn-cookie--muted" type="button" @click="rejectAll">
              Rechazar
            </button>
            <button class="btn-cookie btn-cookie--accept" type="button" @click="acceptAll">
              Aceptar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ Modal de preferencias SIEMPRE disponible si showModal -->
    <div v-if="showModal" class="cookie-modal-backdrop" @click.self="closePreferences">
      <div class="cookie-modal" role="dialog" aria-modal="true" aria-label="Preferencias de cookies">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Preferencias de cookies</h5>
          <button class="cookie-x" type="button" aria-label="Cerrar" @click="closePreferences">×</button>
        </div>

        <div class="cookie-row">
          <div class="cookie-switch">
            <b>Necesarias</b>
            <div class="text-muted small">Siempre activas para que Quanty funcione.</div>
          </div>
          <div><span class="badge bg-success">ON</span></div>
        </div>

        <div class="cookie-row">
          <div class="cookie-switch">
            <b>Analíticas</b>
            <div class="text-muted small">Nos ayudan a medir visitas y mejorar el producto.</div>
          </div>
          <div class="cookie-controls">
            <span class="badge" :class="draft.analytics ? 'bg-success' : 'bg-secondary'">
              {{ draft.analytics ? 'ON' : 'OFF' }}
            </span>
            
            <input class="form-check-input q-switch" type="checkbox" role="switch" v-model="draft.analytics" />
  
          </div>

        </div>

        <div class="cookie-row">
          <div class="cookie-switch">
            <b>Marketing</b>
            <div class="text-muted small">Publicidad/remarketing.</div>
          </div>
         <div class="cookie-controls">
            <span class="badge" :class="draft.marketing ? 'bg-success' : 'bg-secondary'">
              {{ draft.marketing ? 'ON' : 'OFF' }}
            </span>
            <input class="form-check-input q-switch" type="checkbox" role="switch" v-model="draft.marketing" />
          </div>

        </div>

        <div class="cookie-row">
          <div class="cookie-switch">
            <b>Preferencias</b>
            <div class="text-muted small">Recuerda configuraciones.</div>
          </div>
         <div class="cookie-controls">
            <span class="badge" :class="draft.preferences ? 'bg-success' : 'bg-secondary'">
              {{ draft.preferences ? 'ON' : 'OFF' }}
            </span>
            <input class="form-check-input q-switch" type="checkbox" role="switch" v-model="draft.preferences" />
          </div>

        </div>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn-cookie btn-cookie--outline" type="button" @click="closePreferences">
            Cancelar
          </button>
          <button class="btn-cookie btn-cookie--accept" type="button" @click="saveDraft">
            Guardar
          </button>
        </div>
      </div>
    </div>

  </div>
</template>


<script>
import { readConsent, writeConsent, applyConsentToGtag } from "@/services/consent";

export default {
  name: "CookieBanner",
  data() {
    return {
      visible: false,
      showModal: false,
      draft: { preferences: false, analytics: false, marketing: false },
      prevBodyOverflow: null,
      quantyOpenPrefsHandler: null,
    };
  },

  mounted() {
    const existing = readConsent();
    if (!existing) {
      this.visible = true;
    } else {
      applyConsentToGtag(existing);
    }

    // ✅ Exponer API global para "Gestionar cookies"
    window.QuantyConsent = {
      showPreferences: () => this.openPreferences(),
      acceptAll: () => this.acceptAll(),
      rejectAll: () => this.rejectAll(),
    };

    // ✅ Listener global (por si el footer dispara evento)
    this.quantyOpenPrefsHandler = () => this.openPreferences();
    window.addEventListener("quanty:open-cookie-preferences", this.quantyOpenPrefsHandler);

    // ✅ Ajusta scroll si ya venimos con modal/banner abierto
    this.syncBodyScroll();
  },

  watch: {
    visible: "syncBodyScroll",
    showModal: "syncBodyScroll",
  },

  beforeUnmount() {
    // Limpieza scroll
    document.body.style.overflow = this.prevBodyOverflow ?? "";

    // Limpieza listener
    if (this.quantyOpenPrefsHandler) {
      window.removeEventListener("quanty:open-cookie-preferences", this.quantyOpenPrefsHandler);
      this.quantyOpenPrefsHandler = null;
    }

    // Limpieza API global
    if (window.QuantyConsent) delete window.QuantyConsent;
  },

  methods: {
    syncBodyScroll() {
      const lock = this.visible || this.showModal;

      if (lock) {
        if (this.prevBodyOverflow === null) {
          this.prevBodyOverflow = document.body.style.overflow ?? "";
        }
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = this.prevBodyOverflow ?? "";
        this.prevBodyOverflow = null;
      }
    },

    onOverlayClick() {
      this.rejectAll();
    },

    closePreferences() {
      this.showModal = false;
    },

    openPreferences() {
      const existing = readConsent();
      this.draft = {
        preferences: !!existing?.preferences,
        analytics: !!existing?.analytics,
        marketing: !!existing?.marketing,
      };
      this.showModal = true;
    },

    acceptAll() {
      const consent = writeConsent({ preferences: true, analytics: true, marketing: true });

      // 1) aplica consent mode
      applyConsentToGtag(consent);

      // 2) fuerza page_view tras aceptar (SPA)
      if (window.gtag && consent.analytics) {
        window.gtag('event', 'page_view');
      }

      this.visible = false;
      this.showModal = false;
    },


    rejectAll() {
      const consent = writeConsent({ preferences: false, analytics: false, marketing: false });
      applyConsentToGtag(consent);
      this.visible = false;
      this.showModal = false;
    },

    saveDraft() {
      const consent = writeConsent(this.draft);

      applyConsentToGtag(consent);

      // Si el usuario acaba de activar analíticas, dispara page_view
      if (window.gtag && consent.analytics) {
        window.gtag('event', 'page_view');
      }

      this.visible = false;
      this.showModal = false;
    },

  },
};
</script>

<style scoped>
/* =========================
   Banner principal centrado
========================= */
.cookie-overlay{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  z-index: 9998;
}

.cookie-center{
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: min(980px, calc(100vw - 28px));
  z-index: 9999;
}

.cookie-card{
  margin: 0 auto;
  background: #0e141f;
  color: #e7edf5;
  border-radius: 14px;
  box-shadow: 0 20px 70px rgba(0,0,0,.55);
  padding: 14px 14px;
  border: 1px solid #1b2532;
}

.cookie-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.cookie-title{ font-weight: 900; }

.cookie-desc{
  font-size: .95rem;
  opacity: .92;
  margin-bottom: 12px;
}

.cookie-link{
  color: #f5a623;
  margin-left: 6px;
  text-decoration: underline;
}

/* ─── Botones del banner (sin depender de Bootstrap) ─── */
.btn-cookie {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: .875rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background .15s, opacity .15s;
  white-space: nowrap;
}
.btn-cookie--outline {
  background: transparent;
  border-color: #243040;
  color: #7c8ba1;
}
.btn-cookie--outline:hover { background: #111926; color: #e7edf5; }

.btn-cookie--muted {
  background: #111926;
  border-color: #1b2532;
  color: #e7edf5;
}
.btn-cookie--muted:hover { background: #1b2532; }

.btn-cookie--accept {
  background: #f5a623;
  border-color: #f5a623;
  color: #140c00;
}
.btn-cookie--accept:hover { background: #f6ae35; }

.cookie-actions{
  display:flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media (max-width: 576px){
  .cookie-actions .btn-cookie{ width: 100%; }
  .cookie-actions{ justify-content: stretch; }
}

/* =========================
   Modal de preferencias
========================= */
.cookie-modal-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  display:flex;
  align-items:center;
  justify-content:center;
  padding: 16px;
  z-index: 10000;
  pointer-events: auto;
}

.cookie-modal{
  width: 100%;
  max-width: 520px;
  background: #0e141f;
  color: #e7edf5;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 18px 60px rgba(0,0,0,.55);
  border: 1px solid #1b2532;
  position: relative;
  z-index: 10001;
  pointer-events: auto;
}

.cookie-modal h5 { color: #e7edf5; }
.cookie-modal .text-muted { color: #7c8ba1 !important; }

.cookie-row{
  display:flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #1b2532;
}
.cookie-row:last-child{ border-bottom: 0; }

.cookie-switch{
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  justify-content:center;
  gap: 2px;
}

.form-check-input{
  width: 44px;
  height: 22px;
  cursor: pointer;
  transform: translateY(2px);
  accent-color: #d11a8d;
}

/* clickable safety */
.cookie-modal input,
.cookie-modal label{
  pointer-events: auto;
}

/* close button */
.cookie-x{
  border:0;
  background:transparent;
  font-size: 22px;
  line-height: 22px;
  opacity:.75;
  color: inherit;
  cursor: pointer;
}
.cookie-x:hover{ opacity: 1; }
.cookie-controls{
  display:flex;
  align-items:center;
  gap: 10px;
  min-width: 140px;
  justify-content:flex-end;
}

/* Fuerza visual ON en verde (independiente del navegador) */
.form-check-input:checked{
  background-color: #198754;
  border-color: #198754;
}
/* =========================
   Quanty Switch (empirulado)
========================= */
.q-switch{
  appearance: none;
  -webkit-appearance: none;
  width: 46px;
  height: 24px;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  border: 1px solid rgba(0,0,0,.15);
  background: #3b4154; /* OFF (oscuro elegante) */
  transition: background .18s ease, border-color .18s ease, box-shadow .18s ease;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
  transform: translateY(2px);
}

/* “Pomo” */
.q-switch::before{
  content: "×";
  position: absolute;
  top: 50%;
  left: 3px;
  transform: translate(0, -50%);
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  color: rgba(0,0,0,.55);
  font-weight: 900;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(0,0,0,.18);
  transition: left .18s ease, color .18s ease, transform .18s ease;
}

/* ON state */
.q-switch:checked{
  background: linear-gradient(90deg, #198754 0%, #22c55e 100%);
  border-color: rgba(25,135,84,.55);
  box-shadow: 0 0 0 3px rgba(34,197,94,.18);
}

.q-switch:checked::before{
  content: "✓";
  left: 25px;
  color: #198754;
}

/* Focus accesible */
.q-switch:focus{
  outline: none;
  box-shadow: 0 0 0 3px rgba(209,26,141,.20); /* glow Quanty */
}

</style>
